import { useEffect } from 'react';
import { useLocalization } from 'contexts/LocalizationContext';
import { ProductRuleItem } from './product-rule-item';
import { Suggestion } from 'shared/ui/suggestion/suggestion';
import cls from './product-rules.module.scss';
import { useCachedContentFreshnessRule } from 'hooks/api/cached/useCachedContentFreshnessRule';
import { useCachedStatisticalEvidenceRule } from 'hooks/api/cached/useCachedStatisticalEvidenceRule';
import { useCachedConversationalWritingRule } from 'hooks/api/cached/useCachedConversationalWritingRule';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { preloadTranslations } from 'services/translationService';
import { RULES_INFO_COMPLETENESS_TRANSLATION_FILE } from 'shared/constants/rules-texts';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { ImprovementBanner } from 'shared/ui/improvement-banner/improvement-banner';
import { UnitSlot } from 'shared/ui/unit-slot/unit-slot';
import { RunBlockKey, useReportProgress } from 'shared/hooks/useReportProgress';
import { RUN_BLOCK_EXPECTED_SECONDS } from 'shared/helpers/reportTimeline';

interface ProductRulesProps {
  productName: string;
  color?: 'gray' | 'white';
  showScore?: boolean;
  showBanner?: boolean;
  productNumber?: number;
  useImprovementVersion?: boolean;
  productId?: string;
}

export const ProductRules: React.FC<ProductRulesProps> = ({
  productName,
  productId,
  productNumber,
  color = 'gray',
  showScore = true,
  showBanner = false,
  useImprovementVersion = false,
}) => {
  const { currentLocale } = useLocalization();
  const { t } = useTranslation();
  const { selectedProject, reportGenerating, testingB, isTrial } =
    useCachedSelectedProject();
  const { blocks, blockStartedAt, isRunActive } = useReportProgress();

  useEffect(() => {
    void preloadTranslations(currentLocale, [
      RULES_INFO_COMPLETENESS_TRANSLATION_FILE,
    ]);
  }, [currentLocale]);

  const {
    ruleData: conversational,
    isLoading: conversationalLoading,
    isLoaded: conversationalLoaded,
  } = useCachedConversationalWritingRule();
  const {
    ruleData: statistical,
    isLoading: statisticalLoading,
    isLoaded: statisticalLoaded,
  } = useCachedStatisticalEvidenceRule();
  const {
    ruleData: content,
    isLoading: contentLoading,
    isLoaded: contentLoaded,
  } = useCachedContentFreshnessRule();
  const serviceConversation =
    conversational?.products?.[productId ?? productName] ??
    conversational?.products?.[productName];
  const serviceStatistic =
    statistical?.products?.[productId ?? productName] ??
    statistical?.products?.[productName];
  const serviceContent =
    content?.products?.[productId ?? productName] ??
    content?.products?.[productName];
  const SuggestionComponent = useImprovementVersion
    ? ProductRuleItem
    : Suggestion;

  const renderRuleCard = (
    block: RunBlockKey,
    labelKey: string,
    legacyLoading: boolean,
    content: React.ReactNode
  ) => {
    if (isRunActive) {
      return (
        <UnitSlot
          unitState={blocks[block]}
          name={t(labelKey)}
          minHeight={useImprovementVersion ? 168 : 192}
          className={useImprovementVersion ? cls.ruleSlot : ''}
          startedAt={blockStartedAt[block]}
          expectedSeconds={RUN_BLOCK_EXPECTED_SECONDS[block]}
        >
          {content}
        </UnitSlot>
      );
    }

    return legacyLoading ? (
      <div className={cls.skeletonWrapper}>
        <Skeleton height={88} />
      </div>
    ) : (
      content
    );
  };

  return (
    <div>
      <div className={cls.plans}>
        {renderRuleCard(
          'conversationalWriting',
          'conversational_content_writing',
          reportGenerating
            ? !conversationalLoaded || !selectedProject
            : conversationalLoading || !selectedProject,
          <SuggestionComponent
            color={color}
            showScore={showScore}
            analysis={serviceConversation?.examples || []}
            title='conversational_content_writing'
            score={serviceConversation?.assessment_score || 1}
            productNumber={productNumber}
            text={serviceConversation?.assessment_explanation || ''}
          />
        )}
        {renderRuleCard(
          'statisticalEvidence',
          'statistical_evidence_integration',
          reportGenerating
            ? !statisticalLoaded || !selectedProject
            : statisticalLoading || !selectedProject,
          <SuggestionComponent
            color={color}
            analysis={serviceStatistic?.examples || []}
            title='statistical_evidence_integration'
            score={serviceStatistic?.assessment_score || 1}
            text={serviceStatistic?.assessment_explanation || ''}
            showScore={showScore}
            restricted={testingB && isTrial}
            productNumber={productNumber}
          />
        )}
        {renderRuleCard(
          'contentFreshness',
          'content_freshness_indicators',
          reportGenerating
            ? !contentLoaded || !selectedProject
            : contentLoading || !selectedProject,
          <SuggestionComponent
            color={color}
            showScore={showScore}
            analysis={serviceContent?.examples || []}
            title='content_freshness_indicators'
            score={serviceContent?.assessment_score || 1}
            text={serviceContent?.assessment_explanation || ''}
            restricted={testingB && isTrial}
            productNumber={productNumber}
          />
        )}
        {showBanner && <ImprovementBanner />}
      </div>
    </div>
  );
};
